import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Check,
  Copy,
  Pencil,
  SlidersHorizontal,
  UserCheck,
  UserPlus,
  UserX,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  BulkUpdateMembersResponse,
  MemberAccountKind,
  MemberListItem,
  MemberRoleKey,
  MemberWritable,
  OrganizationInviteResponse,
  PendingMemberListItem,
  PossibleDuplicateMatch,
  TeamResponse,
  TrainingGroupResponse,
} from '@bm/contracts';
import { MEMBER_ACCOUNT_KIND_LABELS, MEMBER_GENDER_LABELS, MEMBER_ROLE_LABELS } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { Modal } from '../components/Modal';

interface Page {
  data: MemberListItem[];
  page: number;
  pageSize: number;
  total: number;
}

/** Visually distinguishes Spieler/Elternteil/Kind in the Mitgliederliste — see
 * `MemberAccountKind` in @bm/contracts for how each value is derived. */
const ACCOUNT_KIND_STYLES: Record<MemberAccountKind, string> = {
  player: 'bg-blue-100 text-blue-700',
  parent: 'bg-purple-100 text-purple-700',
  dependent: 'bg-amber-100 text-amber-700',
};

/** Jede toggle-bare Spalte der Mitgliederliste — Name selbst ist immer
 * sichtbar (Link zur Detailseite) und steht deshalb nicht in dieser Liste.
 * Reine Anzeige-Spalten (Kontoart, Mannschaft(en), Trainingsgruppe(n),
 * Erstellt/Aktualisiert am) sind hier zwar toggle-, aber nicht bearbeitbar —
 * siehe `EDITABLE_FIELD_LABELS` unten für die tatsächlich (inline und im
 * Sammel-Bearbeiten-Dialog) editierbaren Felder. */
type ColumnKey =
  | 'accountKind'
  | 'email'
  | 'roleKey'
  | 'teamNames'
  | 'trainingGroupNames'
  | 'gender'
  | 'birthDate'
  | 'emergencyPhone'
  | 'selfRegistrationEnabled'
  | 'createdAt'
  | 'updatedAt';

const ALL_COLUMNS: ColumnKey[] = [
  'accountKind',
  'email',
  'roleKey',
  'teamNames',
  'trainingGroupNames',
  'gender',
  'birthDate',
  'emergencyPhone',
  'selfRegistrationEnabled',
  'createdAt',
  'updatedAt',
];

const COLUMN_LABELS: Record<ColumnKey, string> = {
  accountKind: 'Kontoart',
  email: 'E-Mail',
  roleKey: 'App-Rolle',
  teamNames: 'Mannschaft(en)',
  trainingGroupNames: 'Trainingsgruppe(n)',
  gender: 'Geschlecht',
  birthDate: 'Geburtstag',
  emergencyPhone: 'Notfall-Telefon',
  selfRegistrationEnabled: 'Spielt/trainiert selbst mit',
  createdAt: 'Erstellt am',
  updatedAt: 'Aktualisiert am',
};

// Deckt sich mit der bisherigen festen Spaltenauswahl, damit sich für
// bestehende Nutzer:innen ohne gespeicherte Auswahl nichts ändert.
const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = ['accountKind', 'email', 'roleKey', 'teamNames', 'trainingGroupNames'];
const COLUMNS_STORAGE_KEY = 'bm:members-grid-columns';

function loadVisibleColumns(): Set<ColumnKey> {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return new Set(DEFAULT_VISIBLE_COLUMNS);
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set(DEFAULT_VISIBLE_COLUMNS);
    return new Set(parsed.filter((c): c is ColumnKey => ALL_COLUMNS.includes(c as ColumnKey)));
  } catch {
    return new Set(DEFAULT_VISIBLE_COLUMNS);
  }
}

/** Nur diese sechs Felder (plus die App-Rolle) lassen sich inline in der
 * Zelle und im Sammel-Bearbeiten-Dialog setzen — bewusst OHNE Name/E-Mail
 * (Name bleibt der Navigations-Link zur Detailseite; dieselbe E-Mail für
 * mehrere Mitglieder auf einmal zu setzen ergäbe nie Sinn), OHNE Passwort
 * (nie über die Liste, eigener Bereich auf der Detailseite) und OHNE
 * Mannschaft/Trainingsgruppe (eigene m:n-Zuordnung mit eigener,
 * reichhaltigerer UI auf der Detailseite, kein einfaches Feld). */
type EditableFieldKey = 'gender' | 'birthDate' | 'emergencyPhone' | 'selfRegistrationEnabled' | 'roleKey';
const EDITABLE_FIELD_LABELS: Record<EditableFieldKey, string> = {
  gender: 'Geschlecht',
  birthDate: 'Geburtstag',
  emergencyPhone: 'Notfall-Telefon',
  selfRegistrationEnabled: 'Spielt/trainiert selbst mit',
  roleKey: 'App-Rolle',
};

const INLINE_CELL_CLASS =
  'w-full min-w-[6.5rem] rounded border border-transparent bg-transparent px-1 py-0.5 text-sm hover:border-slate-200 focus:border-brand-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-accent';

/** Text-/Datumseingabe für eine einzelne Zelle — unkontrolliert
 * (`defaultValue` statt `value`), speichert erst bei `onBlur` und nur, wenn
 * sich der Wert tatsächlich geändert hat. `key={value}` erzwingt nach einem
 * externen Refetch (z. B. nach dem Speichern) ein Remount mit dem neuen
 * Serverwert statt eines veralteten lokalen Zustands. */
function InlineTextCell({
  value,
  onSave,
  type = 'text',
}: {
  value: string;
  onSave: (next: string) => void;
  type?: 'text' | 'email' | 'date';
}) {
  return (
    <input
      key={value}
      type={type}
      defaultValue={value}
      onBlur={(e) => {
        if (e.target.value !== value) onSave(e.target.value);
      }}
      className={INLINE_CELL_CLASS}
    />
  );
}

function InlineGenderCell({ value, onSave }: { value: MemberWritable['gender']; onSave: (next: MemberWritable['gender']) => void }) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onSave(e.target.value === '' ? null : (e.target.value as 'm' | 'w'))}
      className={INLINE_CELL_CLASS}
    >
      <option value="">—</option>
      {Object.entries(MEMBER_GENDER_LABELS).map(([key, label]) => (
        <option key={key} value={key}>
          {label}
        </option>
      ))}
    </select>
  );
}

function InlineCheckboxCell({ checked, onSave }: { checked: boolean; onSave: (next: boolean) => void }) {
  return <input type="checkbox" checked={checked} onChange={(e) => onSave(e.target.checked)} />;
}

function InlineRoleCell({ value, onSave }: { value: MemberRoleKey; onSave: (next: MemberRoleKey) => void }) {
  return (
    <select value={value} onChange={(e) => onSave(e.target.value as MemberRoleKey)} className={INLINE_CELL_CLASS}>
      {Object.entries(MEMBER_ROLE_LABELS).map(([key, label]) => (
        <option key={key} value={key}>
          {label}
        </option>
      ))}
    </select>
  );
}

/** Inhalt einer einzelnen Spalte für genau ein Mitglied — von Desktop-Tabelle
 * UND Mobile-Kartenliste (§ Nutzeranfrage „Mitgliederliste … responsive, gut
 * für mobile Geräte") gemeinsam genutzt, damit beide Ansichten exakt dieselbe
 * Anzeige-/Bearbeitungslogik je Spalte haben, ohne sie doppelt zu pflegen. */
function renderColumnValue(
  col: ColumnKey,
  m: MemberListItem,
  canEdit: boolean,
  save: (patch: Record<string, unknown>) => void,
): ReactNode {
  switch (col) {
    case 'accountKind':
      return m.accountKind ? (
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ACCOUNT_KIND_STYLES[m.accountKind]}`}>
          {MEMBER_ACCOUNT_KIND_LABELS[m.accountKind]}
        </span>
      ) : (
        '—'
      );
    case 'email':
      return canEdit ? (
        <InlineTextCell value={m.email ?? ''} type="email" onSave={(v) => save({ email: v || undefined })} />
      ) : (
        (m.email ?? '—')
      );
    case 'roleKey':
      return canEdit && m.roleKey ? (
        <InlineRoleCell value={m.roleKey} onSave={(v) => save({ roleKey: v })} />
      ) : m.roleKey ? (
        MEMBER_ROLE_LABELS[m.roleKey]
      ) : (
        '—'
      );
    case 'teamNames':
      return m.teamNames.join(', ') || '—';
    case 'trainingGroupNames':
      return m.trainingGroupNames.join(', ') || '—';
    case 'gender':
      return canEdit ? (
        <InlineGenderCell value={m.gender} onSave={(v) => save({ gender: v })} />
      ) : (
        (m.gender ? MEMBER_GENDER_LABELS[m.gender] : '—')
      );
    case 'birthDate':
      return canEdit ? (
        <InlineTextCell value={m.birthDate ?? ''} type="date" onSave={(v) => save({ birthDate: v || null })} />
      ) : (
        (m.birthDate ?? '—')
      );
    case 'emergencyPhone':
      return canEdit ? (
        <InlineTextCell value={m.emergencyPhone ?? ''} onSave={(v) => save({ emergencyPhone: v || null })} />
      ) : (
        (m.emergencyPhone ?? '—')
      );
    case 'selfRegistrationEnabled':
      return canEdit ? (
        <InlineCheckboxCell checked={m.selfRegistrationEnabled} onSave={(v) => save({ selfRegistrationEnabled: v })} />
      ) : m.selfRegistrationEnabled ? (
        'Ja'
      ) : (
        '—'
      );
    case 'createdAt':
      return new Date(m.createdAt).toLocaleDateString('de-DE');
    case 'updatedAt':
      return new Date(m.updatedAt).toLocaleDateString('de-DE');
  }
}

/** Ein Spalten-Filter der Mitgliederliste (§ Nutzeranfrage „ein Filter für
 * die Spalten, z. B. nur 3. Mannschaft anzeigen") — ein einfaches natives
 * `<select>` statt eines Mehrfachauswahl-Dropdowns wie
 * `GroupFilterDropdown`/`WeekdayFilterDropdown` (Training/Saison): hier
 * reicht „genau eine Mannschaft/Rolle/…", das Beispiel in der Anfrage selbst
 * ist bereits ein Einzelwert. Serverseitig gefiltert (`GET /members` nimmt
 * `teamId`/`trainingGroupId`/`roleKey`/`accountKind` entgegen), nicht nur
 * clientseitig über die aktuell geladene Seite — sonst würde „3. Mannschaft"
 * nur innerhalb der gerade sichtbaren 25 Zeilen gefiltert, nicht über die
 * ganze Mitgliederliste. */
function ColumnFilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`shrink-0 rounded-md border px-2.5 py-1.5 text-sm ${
        value ? 'border-brand-accent bg-brand-accent/5 text-brand-accent' : 'border-slate-300 text-slate-700'
      }`}
    >
      <option value="">{label}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Spalten-Auswahl — welche Felder in der Tabelle angezeigt werden, pro
 * Browser in `localStorage` gemerkt. Eigenständiges Dropdown (kein
 * gemeinsames Popover-UI-Primitive im Projekt vorhanden), Backdrop schließt
 * bei Klick daneben. */
function ColumnPicker({
  visible,
  onToggle,
  onClose,
}: {
  visible: Set<ColumnKey>;
  onToggle: (col: ColumnKey) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Spalten anzeigen</p>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {ALL_COLUMNS.map((col) => (
            <label key={col} className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-slate-50">
              <input type="checkbox" checked={visible.has(col)} onChange={() => onToggle(col)} />
              {COLUMN_LABELS[col]}
            </label>
          ))}
        </div>
      </div>
    </>
  );
}

/** Sammel-Bearbeiten: ein Feld auswählen, einen Wert eintragen, für alle
 * ausgewählten Mitglieder übernehmen — pro Aufruf genau ein Feld, wiederholt
 * anwendbar (statt eines vollen Diff-Formulars für alle sechs Felder
 * gleichzeitig, was hier vom Bedarf her nicht nötig ist). */
function BulkEditModal({
  count,
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  count: number;
  onClose: () => void;
  onSubmit: (patch: Partial<MemberWritable> & { roleKey?: MemberRoleKey }) => void;
  isPending: boolean;
  error: string | null;
}) {
  const [field, setField] = useState<EditableFieldKey>('gender');
  const [gender, setGender] = useState<'' | 'm' | 'w'>('');
  const [birthDate, setBirthDate] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [selfRegistrationEnabled, setSelfRegistrationEnabled] = useState(false);
  const [roleKey, setRoleKey] = useState<MemberRoleKey>('member');

  function buildPatch(): Partial<MemberWritable> & { roleKey?: MemberRoleKey } {
    switch (field) {
      case 'gender':
        return { gender: gender === '' ? null : gender };
      case 'birthDate':
        return { birthDate: birthDate || null };
      case 'emergencyPhone':
        return { emergencyPhone: emergencyPhone || null };
      case 'selfRegistrationEnabled':
        return { selfRegistrationEnabled };
      case 'roleKey':
        return { roleKey };
    }
  }

  return (
    <Modal title="Mehrere Mitglieder bearbeiten" subtitle={`${count} ausgewählt`} icon={<Pencil size={18} />} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(buildPatch());
        }}
      >
        <Field label="Feld">
          <select
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={field}
            onChange={(e) => setField(e.target.value as EditableFieldKey)}
          >
            {Object.entries(EDITABLE_FIELD_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        {field === 'gender' && (
          <Field label="Neuer Wert">
            <select
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={gender}
              onChange={(e) => setGender(e.target.value as '' | 'm' | 'w')}
            >
              <option value="">— (kein Geschlecht)</option>
              {Object.entries(MEMBER_GENDER_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {field === 'birthDate' && (
          <Field label="Neuer Wert">
            <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
          </Field>
        )}
        {field === 'emergencyPhone' && (
          <Field label="Neuer Wert">
            <Input
              type="text"
              value={emergencyPhone}
              onChange={(e) => setEmergencyPhone(e.target.value)}
              placeholder="leer lassen zum Löschen"
            />
          </Field>
        )}
        {field === 'selfRegistrationEnabled' && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selfRegistrationEnabled}
              onChange={(e) => setSelfRegistrationEnabled(e.target.checked)}
            />
            Spielt/trainiert selbst mit
          </label>
        )}
        {field === 'roleKey' && (
          <Field label="Neuer Wert">
            <select
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={roleKey}
              onChange={(e) => setRoleKey(e.target.value as MemberRoleKey)}
            >
              {Object.entries(MEMBER_ROLE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        )}

        {error && <Alert>{error}</Alert>}

        <div className="flex gap-2">
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Wird übernommen…' : `Für ${count} Mitglied(er) übernehmen`}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Generates (or fetches the still-valid) org-wide self-registration link and
 * shows it with a one-click copy — the admin shares it out-of-band (chat,
 * e-mail, …), nothing is sent from here. */
function InvitePanel({ onClose }: { onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const generate = useMutation({
    mutationFn: () => api<OrganizationInviteResponse>('/org/invites', { method: 'POST', orgScoped: true }),
  });
  const revoke = useMutation({
    mutationFn: () => api('/org/invites', { method: 'DELETE', orgScoped: true }),
    onSuccess: () => generate.mutate(),
  });

  useEffect(() => {
    generate.mutate();
  }, []);

  return (
    <Card title="Mitglied/Benutzer einladen">
      <p className="mb-3 text-sm text-slate-500">
        Diesen Link an eine Person schicken (Chat, E-Mail, …) — beim Öffnen kann sie sich mit
        eigenen Zugangsdaten selbst als Mitglied dieses Vereins registrieren. Der Link gilt nur
        für diesen Verein{generate.data ? ` und bis ${new Date(generate.data.expiresAt).toLocaleDateString('de-DE')}` : ''}.
      </p>
      {generate.isPending && <p className="text-sm text-slate-400">Link wird erstellt…</p>}
      {generate.isError && <Alert>Link konnte nicht erstellt werden.</Alert>}
      {revoke.isError && <Alert>Link konnte nicht ungültig gemacht werden.</Alert>}
      {generate.data && (
        <div className="flex items-center gap-2">
          <Input readOnly value={generate.data.url} className="font-mono text-xs" />
          <Button
            type="button"
            variant="ghost"
            className="shrink-0 gap-1.5"
            onClick={() => {
              void navigator.clipboard.writeText(generate.data!.url);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Kopiert' : 'Kopieren'}
          </Button>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Schließen
        </Button>
        {generate.data && (
          <Button
            type="button"
            variant="danger"
            disabled={revoke.isPending}
            onClick={() => {
              if (confirm('Diesen Link ungültig machen? Ein neuer wird sofort erzeugt.')) revoke.mutate();
            }}
          >
            {revoke.isPending ? 'Wird ungültig gemacht…' : 'Link ungültig machen'}
          </Button>
        )}
      </div>
    </Card>
  );
}

/** "Möglicherweise bereits Mitglied"-Hinweis (siehe docs/members.md,
 * "Mögliche Dopplung bei Selbst-Registrierung erkennen") — nur hier, im
 * admin-seitigen Freischaltungs-Panel sichtbar, nie auf dem öffentlichen
 * Registrierungsformular (würde sonst den Namen eines bestehenden
 * Mitglieds an anonyme Besucher verraten). Radiogruppe statt Checkbox,
 * damit "Verknüpfen" vs. "Als neues Mitglied" immer eine explizite, bewusste
 * Wahl ist — Default ist "neu", nie automatisch verknüpft. */
function DuplicateHint({
  match,
  name,
  merging,
  onChange,
}: {
  match: PossibleDuplicateMatch;
  name: string;
  merging: boolean;
  onChange: (merge: boolean) => void;
}) {
  return (
    <div className="mt-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
      <p className="flex items-start gap-1.5">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>
          Möglicherweise bereits Mitglied: <strong>{match.firstName} {match.lastName}</strong>, angelegt am{' '}
          {new Date(match.createdAt).toLocaleDateString('de-DE')}
          {match.birthDateMatches === true && ' — Geburtstag stimmt überein'}
          {match.birthDateMatches === false && ' — Geburtstag weicht ab'}.
        </span>
      </p>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 pl-[20px]">
        <label className="flex items-center gap-1.5">
          <input type="radio" name={name} checked={merging} onChange={() => onChange(true)} />
          Mit bestehendem Profil verknüpfen
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" name={name} checked={!merging} onChange={() => onChange(false)} />
          Als neues Mitglied anlegen
        </label>
      </div>
    </div>
  );
}

/** Members who registered themselves via an invite link and are waiting for
 * a club admin to review and grant them real access (`pending_approval`). */
function PendingApprovalPanel({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['members', 'pending-approval'],
    queryFn: () => api<PendingMemberListItem[]>('/members/pending-approval', { orgScoped: true }),
  });

  // Per Freischaltungs-Zeile die gewählte Verknüpfungs-Entscheidung (falls
  // die Zeile bzw. ihr Kind einen possibleDuplicate-Treffer hat) —
  // undefined = "als neues Mitglied anlegen", der sichere Default.
  const [choices, setChoices] = useState<Record<string, { self?: string; child?: string }>>({});

  const approve = useMutation({
    mutationFn: ({ memberId, mergeSelfWithMemberId, mergeChildWithMemberId }: { memberId: string; mergeSelfWithMemberId?: string; mergeChildWithMemberId?: string }) =>
      api(`/members/${memberId}/approve`, {
        method: 'POST',
        orgScoped: true,
        body: { mergeSelfWithMemberId, mergeChildWithMemberId },
      }),
    // Auch die Hauptliste invalidieren (Präfix-Match erfasst
    // ['members', { q, page }]), nicht nur diese Freischaltungs-Liste — sonst
    // taucht das frisch freigeschaltete Mitglied (bzw. bei einem Elternkonto
    // das materialisierte Kind) dort erst nach einem manuellen Neuladen der
    // Seite auf.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members', 'pending-approval'] });
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
  const reject = useMutation({
    mutationFn: (memberId: string) => api(`/members/${memberId}/reject`, { method: 'POST', orgScoped: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members', 'pending-approval'] });
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });

  return (
    <Card title="Wartet auf Freischaltung">
      <p className="mb-3 text-sm text-slate-500">
        Diese Personen haben sich über den Einladungslink selbst registriert und warten auf
        deine Freigabe — bis dahin haben sie keinen Zugriff auf den Verein.
      </p>
      {isLoading && <p className="text-sm text-slate-400">Wird geladen…</p>}
      {data?.length === 0 && <p className="text-sm text-slate-500">Niemand wartet gerade.</p>}
      <div className="divide-y divide-slate-100">
        {data?.map((p) => {
          const rowChoice = choices[p.memberId] ?? {};
          return (
            <div key={p.memberId} className="py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    {p.firstName} {p.lastName}
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-xs font-normal text-slate-500">
                      {MEMBER_ACCOUNT_KIND_LABELS[p.accountKind]}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">{p.email}</div>
                  {p.accountKind === 'parent' && (
                    <div className="text-xs text-slate-500">
                      Kind{p.pendingChildren.length > 1 ? 'er' : ''}:{' '}
                      {p.pendingChildren.map((c) => `${c.firstName} ${c.lastName}`).join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="gap-1.5"
                    disabled={approve.isPending || reject.isPending}
                    onClick={() =>
                      approve.mutate({
                        memberId: p.memberId,
                        mergeSelfWithMemberId: rowChoice.self,
                        mergeChildWithMemberId: rowChoice.child,
                      })
                    }
                  >
                    <UserCheck size={16} /> Freischalten
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="gap-1.5 text-red-700 hover:bg-red-50"
                    disabled={approve.isPending || reject.isPending}
                    onClick={() => {
                      if (
                        confirm(
                          `Anfrage von ${p.firstName} ${p.lastName} ablehnen? Das Mitglied und — falls dafür extra ein neues Konto angelegt wurde — auch das Konto werden dabei vollständig gelöscht.`,
                        )
                      ) {
                        reject.mutate(p.memberId);
                      }
                    }}
                  >
                    <UserX size={16} /> Ablehnen
                  </Button>
                </div>
              </div>

              {p.possibleDuplicate && (
                <DuplicateHint
                  match={p.possibleDuplicate}
                  name={`dup-self-${p.memberId}`}
                  merging={rowChoice.self === p.possibleDuplicate.memberId}
                  onChange={(merge) =>
                    setChoices((c) => ({
                      ...c,
                      [p.memberId]: { ...c[p.memberId], self: merge ? p.possibleDuplicate!.memberId : undefined },
                    }))
                  }
                />
              )}

              {p.pendingChildren.map(
                (child) =>
                  child.possibleDuplicate && (
                    <DuplicateHint
                      key={child.id}
                      match={child.possibleDuplicate}
                      name={`dup-child-${child.id}`}
                      merging={rowChoice.child === child.possibleDuplicate.memberId}
                      onChange={(merge) =>
                        setChoices((c) => ({
                          ...c,
                          [p.memberId]: { ...c[p.memberId], child: merge ? child.possibleDuplicate!.memberId : undefined },
                        }))
                      }
                    />
                  ),
              )}
            </div>
          );
        })}
      </div>
      <Button type="button" variant="ghost" className="mt-3" onClick={onClose}>
        Schließen
      </Button>
    </Card>
  );
}

export function MembersPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  // Spalten-Filter (§ Nutzeranfrage) — jeweils '' = kein Filter.
  const [teamFilter, setTeamFilter] = useState('');
  const [trainingGroupFilter, setTrainingGroupFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [accountKindFilter, setAccountKindFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const { data: pending } = useQuery({
    queryKey: ['members', 'pending-approval'],
    queryFn: () => api<PendingMemberListItem[]>('/members/pending-approval', { orgScoped: true }),
    enabled: can('members.manage'),
  });
  // Optionen für die Mannschaft-/Trainingsgruppe-Filter — dieselben Listen,
  // die auch die Zuordnungs-UI auf `MemberDetailPage.tsx` nutzt.
  const { data: teamsPage } = useQuery({
    queryKey: ['teams', 'all'],
    queryFn: () => api<{ data: TeamResponse[] }>('/teams?pageSize=200', { orgScoped: true }),
  });
  const { data: trainingGroups } = useQuery({
    queryKey: ['training', 'groups'],
    queryFn: () => api<TrainingGroupResponse[]>('/training/groups', { orgScoped: true }),
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(loadVisibleColumns);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditResult, setBulkEditResult] = useState<string | null>(null);
  const [inlineEditError, setInlineEditError] = useState<string | null>(null);
  const canEdit = can('members.edit');

  function toggleColumn(col: ColumnKey) {
    setVisibleColumns((cur) => {
      const next = new Set(cur);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  }

  const { data } = useQuery({
    queryKey: ['members', { q, page, teamFilter, trainingGroupFilter, roleFilter, accountKindFilter }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), q });
      if (teamFilter) params.set('teamId', teamFilter);
      if (trainingGroupFilter) params.set('trainingGroupId', trainingGroupFilter);
      if (roleFilter) params.set('roleKey', roleFilter);
      if (accountKindFilter) params.set('accountKind', accountKindFilter);
      return api<Page>(`/members?${params}`, { orgScoped: true });
    },
  });

  const updateField = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api(`/members/${id}`, { method: 'PATCH', orgScoped: true, body: patch }),
    onSuccess: () => {
      setInlineEditError(null);
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (err) =>
      setInlineEditError(err instanceof ApiError ? err.message : 'Änderung konnte nicht gespeichert werden.'),
  });

  const bulkEdit = useMutation({
    mutationFn: (patch: Partial<MemberWritable> & { roleKey?: MemberRoleKey }) =>
      api<BulkUpdateMembersResponse>('/members/bulk', {
        method: 'PATCH',
        orgScoped: true,
        body: { memberIds: [...selected], patch },
      }),
    onSuccess: (res) => {
      setShowBulkEdit(false);
      setSelected(new Set());
      setBulkEditResult(
        res.failed.length > 0
          ? `${res.updated} von ${res.updated + res.failed.length} Mitgliedern aktualisiert. Fehlgeschlagen: ${res.failed
              .map((f) => f.message)
              .join('; ')}`
          : null,
      );
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleKey, setRoleKey] = useState<MemberRoleKey>('member');

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/members', {
        method: 'POST',
        orgScoped: true,
        body: { firstName: first, lastName: last, email, password, roleKey },
      }),
    onSuccess: () => {
      setFirst('');
      setLast('');
      setEmail('');
      setPassword('');
      setRoleKey('member');
      setShowCreate(false);
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });

  const deleteSelected = useMutation({
    mutationFn: async () => {
      const ids = [...selected];
      const results = await Promise.allSettled(
        ids.map((id) => api(`/members/${id}`, { method: 'DELETE', orgScoped: true })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { failed, total: ids.length };
    },
    onSuccess: ({ failed, total }) => {
      setSelected(new Set());
      setDeleteError(
        failed > 0 ? `${failed} von ${total} Mitgliedern konnten nicht gelöscht werden.` : null,
      );
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const pageIds = data?.data.map((m) => m.id) ?? [];
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  function toggleAll() {
    setSelected((s) => {
      const next = new Set(s);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }
  function toggleOne(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="hidden text-xl font-semibold md:block">Mitglieder</h1>
        <div className="flex flex-wrap gap-2">
          {canEdit && selected.size > 0 && (
            <Button variant="ghost" className="gap-1.5" onClick={() => setShowBulkEdit(true)}>
              <Pencil size={16} /> Bearbeiten ({selected.size})
            </Button>
          )}
          {can('members.delete') && selected.size > 0 && (
            <Button
              variant="danger"
              onClick={() => {
                if (confirm(`${selected.size} Mitglied(er) wirklich löschen?`)) {
                  setDeleteError(null);
                  deleteSelected.mutate();
                }
              }}
              disabled={deleteSelected.isPending}
            >
              Mitglied löschen ({selected.size})
            </Button>
          )}
          {can('members.create') && (
            <Button variant="ghost" className="gap-1.5" onClick={() => setShowInvite((s) => !s)}>
              <UserPlus size={16} /> Mitglied/Benutzer einladen
            </Button>
          )}
          {can('members.manage') && (
            <Button
              variant="ghost"
              className={`gap-1.5 ${pending && pending.length > 0 ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : ''}`}
              onClick={() => setShowPending((s) => !s)}
            >
              <UserCheck size={16} />
              Wartet auf Freischaltung
              {!!pending?.length && (
                <span className="rounded-full bg-amber-600 px-1.5 py-0.5 text-xs font-semibold text-white">
                  {pending.length}
                </span>
              )}
            </Button>
          )}
          {can('members.create') && (
            <Button onClick={() => setShowCreate((s) => !s)}>Neues Mitglied anlegen</Button>
          )}
        </div>
      </div>

      {deleteError && <Alert>{deleteError}</Alert>}
      {inlineEditError && <Alert>{inlineEditError}</Alert>}
      {bulkEditResult && <Alert>{bulkEditResult}</Alert>}

      {showBulkEdit && canEdit && (
        <BulkEditModal
          count={selected.size}
          onClose={() => setShowBulkEdit(false)}
          onSubmit={(patch) => bulkEdit.mutate(patch)}
          isPending={bulkEdit.isPending}
          error={bulkEdit.isError ? (bulkEdit.error instanceof ApiError ? bulkEdit.error.message : 'Übernehmen fehlgeschlagen.') : null}
        />
      )}

      {showPending && can('members.manage') && <PendingApprovalPanel onClose={() => setShowPending(false)} />}

      {showInvite && can('members.create') && <InvitePanel onClose={() => setShowInvite(false)} />}

      {showCreate && can('members.create') && (
        <Card title="Neues Mitglied">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Vorname">
              <Input value={first} onChange={(e) => setFirst(e.target.value)} required />
            </Field>
            <Field label="Nachname">
              <Input value={last} onChange={(e) => setLast(e.target.value)} required />
            </Field>
            <Field label="E-Mail (für Login)">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Field>
            <Field label="Passwort">
              <Input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={10}
                placeholder="mind. 10 Zeichen"
                required
              />
            </Field>
            <Field label="App-Rolle">
              <select
                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={roleKey}
                onChange={(e) => setRoleKey(e.target.value as MemberRoleKey)}
              >
                {Object.entries(MEMBER_ROLE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Button type="submit" disabled={create.isPending}>
              Anlegen
            </Button>
          </form>
          <p className="mt-2 text-xs text-slate-400">
            Legt sofort ein aktives Konto mit den angegebenen Zugangsdaten an. Existiert die
            E-Mail bereits als Konto, wird stattdessen dieses Konto als Mitglied verknüpft (das
            eingegebene Passwort bleibt dann unangetastet).
          </p>
          {create.isError && (
            <Alert>{create.error instanceof ApiError ? create.error.message : 'Anlegen fehlgeschlagen.'}</Alert>
          )}
        </Card>
      )}

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="min-w-[12rem] flex-1">
            <Input
              placeholder="Suche (Name, E-Mail)"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
            />
          </div>
          <ColumnFilterSelect
            label="Alle Mannschaften"
            value={teamFilter}
            onChange={(v) => {
              setPage(1);
              setTeamFilter(v);
            }}
            options={(teamsPage?.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          />
          <ColumnFilterSelect
            label="Alle Trainingsgruppen"
            value={trainingGroupFilter}
            onChange={(v) => {
              setPage(1);
              setTrainingGroupFilter(v);
            }}
            options={(trainingGroups ?? []).map((g) => ({ value: g.id, label: g.name }))}
          />
          <ColumnFilterSelect
            label="Alle App-Rollen"
            value={roleFilter}
            onChange={(v) => {
              setPage(1);
              setRoleFilter(v);
            }}
            options={Object.entries(MEMBER_ROLE_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <ColumnFilterSelect
            label="Alle Kontoarten"
            value={accountKindFilter}
            onChange={(v) => {
              setPage(1);
              setAccountKindFilter(v);
            }}
            options={Object.entries(MEMBER_ACCOUNT_KIND_LABELS).map(([value, label]) => ({ value, label }))}
          />
          {(teamFilter || trainingGroupFilter || roleFilter || accountKindFilter) && (
            <Button
              type="button"
              variant="ghost"
              className="shrink-0 text-xs"
              onClick={() => {
                setPage(1);
                setTeamFilter('');
                setTrainingGroupFilter('');
                setRoleFilter('');
                setAccountKindFilter('');
              }}
            >
              Filter zurücksetzen
            </Button>
          )}
          <div className="relative ml-auto shrink-0">
            <Button type="button" variant="ghost" className="gap-1.5" onClick={() => setShowColumnPicker((s) => !s)}>
              <SlidersHorizontal size={16} /> Spalten
            </Button>
            {showColumnPicker && (
              <ColumnPicker
                visible={visibleColumns}
                onToggle={toggleColumn}
                onClose={() => setShowColumnPicker(false)}
              />
            )}
          </div>
        </div>
        {/* Desktop: klassische Tabelle, unverändert. Mobile (`md:hidden`
         * unten): eine Kartenliste statt der Tabelle — eine horizontal
         * scrollende, teils über 1000px breite Tabelle (bis zu elf
         * zuschaltbare Spalten) war auf Handy-Breite praktisch unbedienbar
         * (§ Nutzeranfrage „Mitgliederliste … responsive, gut für mobile
         * Geräte"). Beide Ansichten teilen sich `renderColumnValue()`, damit
         * Anzeige **und** Inline-Bearbeitung exakt gleich bleiben — nur das
         * Layout drumherum unterscheidet sich. */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                {(can('members.delete') || canEdit) && (
                  <th className="w-8 py-2">
                    <input type="checkbox" checked={allOnPageSelected} onChange={toggleAll} />
                  </th>
                )}
                <th className="py-2 pr-3">Name</th>
                {ALL_COLUMNS.filter((c) => visibleColumns.has(c)).map((col) => (
                  <th key={col} className="py-2 pr-3">
                    {COLUMN_LABELS[col]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.data.map((m) => {
                const save = (patch: Record<string, unknown>) => updateField.mutate({ id: m.id, patch });
                return (
                  <tr key={m.id}>
                    {(can('members.delete') || canEdit) && (
                      <td className="py-2">
                        <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleOne(m.id)} />
                      </td>
                    )}
                    <td className="py-2 pr-3">
                      <Link to="/members/$id" params={{ id: m.id }} className="text-brand-accent">
                        {m.firstName} {m.lastName}
                      </Link>
                    </td>
                    {ALL_COLUMNS.filter((c) => visibleColumns.has(c)).map((col) => (
                      <td key={col} className="py-2 pr-3">
                        {renderColumnValue(col, m, canEdit, save)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="divide-y divide-slate-100 md:hidden">
          {data?.data.map((m) => {
            const save = (patch: Record<string, unknown>) => updateField.mutate({ id: m.id, patch });
            const otherColumns = ALL_COLUMNS.filter((c) => c !== 'accountKind' && visibleColumns.has(c));
            return (
              <div key={m.id} className="flex items-start gap-3 py-3">
                {(can('members.delete') || canEdit) && (
                  <input
                    type="checkbox"
                    checked={selected.has(m.id)}
                    onChange={() => toggleOne(m.id)}
                    className="mt-1 shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Link
                      to="/members/$id"
                      params={{ id: m.id }}
                      className="truncate text-sm font-semibold text-brand-accent"
                    >
                      {m.firstName} {m.lastName}
                    </Link>
                    {visibleColumns.has('accountKind') && m.accountKind && (
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${ACCOUNT_KIND_STYLES[m.accountKind]}`}
                      >
                        {MEMBER_ACCOUNT_KIND_LABELS[m.accountKind]}
                      </span>
                    )}
                  </div>
                  {otherColumns.length > 0 && (
                    <div className="mt-1.5 space-y-1">
                      {otherColumns.map((col) => (
                        <div key={col} className="flex items-baseline gap-2 text-xs">
                          <span className="w-28 shrink-0 text-slate-400">{COLUMN_LABELS[col]}</span>
                          <span className="min-w-0 flex-1 text-slate-600">
                            {renderColumnValue(col, m, canEdit, save)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
          <span>{data?.total ?? 0} Mitglieder</span>
          <span className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ‹
            </Button>
            <span className="py-2">
              {page} / {pages}
            </span>
            <Button variant="ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              ›
            </Button>
          </span>
        </div>
      </Card>
    </div>
  );
}
